import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { KycModal } from '@/features/auth/components/KycModal';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { KycData } from '@/features/auth/config/kycSteps.config';

/**
 * Global KYC Modal Manager
 *
 * Handles opening the KYC modal based on URL query parameters.
 * This allows any page to trigger the modal by navigating with specific query params:
 *
 * Query Parameters:
 * - openKycModal=true       : Triggers modal opening
 * - kycModalTab=login|signup : Sets initial tab (maps to auth-method step)
 * - initialStepId=<stepId>  : Directly sets the initial step
 * - resetToken=<token>      : Passes reset token for password reset flow
 *
 * Entry points that use this:
 * - AuthModalRedirect (login/signup pages)
 * - CallbackPage (OAuth flow)
 * - ResetPasswordPage (password reset emails)
 * - Header (KYC progress button)
 */
export function KycModalManager() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isOpen, setIsOpen] = useState(false);
    const modalProcessedRef = useRef(false);

    // Capture params once when modal opens (don't read on every render)
    const capturedParamsRef = useRef<{
        initialStepId?: string | null;
        resetToken?: string | null;
        kycModalTab?: string | null;
    }>({});

    // Read query params directly from URL
    const initialStepId = searchParams.get('initialStepId');
    const resetToken = searchParams.get('resetToken');
    const shouldOpenModal = searchParams.get('openKycModal') === 'true' || (initialStepId && resetToken);
    const kycModalTab = searchParams.get('kycModalTab'); // 'login' or 'signup'

    // Determine the step ID based on CAPTURED params (frozen at modal open time)
    const getInitialStepId = (): string | undefined => {
        // Use captured params, not live URL params
        const captured = capturedParamsRef.current;

        // Direct step ID takes precedence
        if (captured.initialStepId) return captured.initialStepId;

        // Map kycModalTab to step ID
        if (captured.kycModalTab === 'login' || captured.kycModalTab === 'signup') {
            return 'auth-method';
        }

        return undefined;
    };

    // Build initial data with any special params (using captured params)
    const getInitialData = (): KycData | undefined => {
        const data: KycData = user || {};

        // Add reset token if present (from captured params)
        if (capturedParamsRef.current.resetToken) {
            data.resetToken = capturedParamsRef.current.resetToken;
        }

        return Object.keys(data).length > 0 ? data : undefined;
    };

    // Open modal when trigger param is detected
    useEffect(() => {
        if (shouldOpenModal && !modalProcessedRef.current) {
            // CAPTURE params immediately before any state updates
            capturedParamsRef.current = {
                initialStepId,
                resetToken,
                kycModalTab,
            };

            modalProcessedRef.current = true;
            setIsOpen(true);

            // Only clean up the trigger param, keep the data params
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('openKycModal');
            setSearchParams(newParams, { replace: true });
        }
    }, [shouldOpenModal, initialStepId, resetToken, kycModalTab, searchParams, setSearchParams]);

    // Clean up all params when modal closes
    useEffect(() => {
        if (!isOpen && modalProcessedRef.current) {
            modalProcessedRef.current = false;

            // Clear captured params
            capturedParamsRef.current = {};

            // Clean up any remaining modal params from URL
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('openKycModal');
            newParams.delete('kycModalTab');
            newParams.delete('initialStepId');
            newParams.delete('resetToken');
            setSearchParams(newParams, { replace: true });
        }
    }, [isOpen, searchParams, setSearchParams]);

    return (
        <KycModal
            key={user?.id || 'guest'}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            initialData={getInitialData()}
            initialStepId={getInitialStepId()}
        />
    );
}
